import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { GenericLabelValue } from "src/app/shared/models/DTO/GenericLabelValue.dto";
import { ChartLegendPosition } from "src/app/shared/models/enums/ChartLegendPosition.enum";
import { SalesSummary } from "src/app/shared/models/SalesSummary.model";

@Component({
	selector: "app-item-dash",
	templateUrl: "./item-dash.component.html",
	styleUrls: ["../../commandcenter.scss"],
})
export class ItemDashComponent implements OnInit, OnChanges {
	@Input() checkDate: string;
	@Output() checkDateChange = new EventEmitter<string>();

	@Input() currentDaySalesSummary: SalesSummary;
	@Output() currentDaySalesSummaryChange = new EventEmitter<SalesSummary>();

	itemDoughnut = {
		options: undefined,
		data: undefined,
		type: "doughnut",
	};

	itemListSortType: GenericLabelValue[] = [
		{ label: "Sales", value: "sales" },
		{ label: "Quantity", value: "quantity" },
	];

	oosCountViewMode: string = "countdown";
	oosCountViewOptions: GenericLabelValue[] = [
		{
			label: "Count",
			value: "countdown",
		},
		{
			label: "OOS",
			value: "outofstock",
		},
	];

	itemListSortTypeSelected: string = "sales";

	metrics = {
		cost: 0,
		costPercent: 0,
		historicalCostPercent: 0,
		outOfStock: 0,
		recordsRemaining: 0,
		lastCheckDate: undefined,
		loadComplete: false,
		highestCostItemSummary: undefined,
	};

	superGroupSummary: {
		id: number;
		name: string;
		amount: number;
		rgCount: number;
		chartColor: string;
	}[] = [];

	constructor(public app: AppService, public venueService: OOVenueService, private focuslink: FocuslinkService) {}

	ngOnInit(): void {
		this.buildSuperGroupDoughnut();
		this.buildMetrics();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.currentDaySalesSummary && this.currentDaySalesSummary?.checkDate) {
			this.buildSuperGroupDoughnut();
			this.buildMetrics();
		}
		if (changes.checkDate) {
			this.metrics.loadComplete = false;
		}
	}

	async buildSuperGroupDoughnut() {
		this.superGroupSummary = [];
		this.currentDaySalesSummary.reportGroupSummary.forEach((rg) => {
			const sgSummary = this.superGroupSummary.find((sg) => sg.id === +rg.superGroup?.id);
			if (!sgSummary) {
				this.superGroupSummary.push({
					id: +rg.superGroup?.id,
					name: rg.superGroup?.name,
					amount: rg.sales,
					rgCount: 1,
					chartColor: this.app.references.chartColorsGoogle[this.superGroupSummary.length],
				});
			} else {
				sgSummary.amount += rg.sales;
				sgSummary.rgCount += 1;
			}
		});
		this.superGroupSummary.sort((a, b) => (a.name > b.name ? 1 : -1));

		const data = {
			labels: this.superGroupSummary.map((i) => i.name),
			datasets: [
				{
					label: "Super Groups",
					data: this.superGroupSummary.map((i) => i.amount),
					fill: true,
					backgroundColor: this.superGroupSummary.map((i) => i.chartColor),
				},
			],
		};
		this.itemDoughnut.data = data;
		this.itemDoughnut.options = this.app.references.chartConfig(ChartLegendPosition.NONE, false, false);
	}

	async buildItemDoughnut(filteredSuperGroupId?: number) {
		const reportGroupsSummary = filteredSuperGroupId
			? this.currentDaySalesSummary.reportGroupSummary.filter((rg) => +rg.superGroup.id === filteredSuperGroupId)
			: this.currentDaySalesSummary.reportGroupSummary;
		reportGroupsSummary.sort((a, b) => (a.name > b.name ? 1 : -1));
		reportGroupsSummary.forEach((job, idx) => {
			job.chartColor = this.app.references.chartColorsGoogle[idx];
		});

		const data = {
			labels: reportGroupsSummary.map((i) => i.name),
			datasets: [
				{
					label: "Report Groups",
					data: reportGroupsSummary.map((i) => i.sales),
					fill: true,
					backgroundColor: reportGroupsSummary.map((i) => i.chartColor),
				},
			],
		};

		this.itemDoughnut.data = data;
		this.itemDoughnut.options = this.app.references.chartConfig(ChartLegendPosition.NONE, false, false);
	}

	async buildMetrics() {
		this.metrics.loadComplete = false;

		const currentDayItemsWithCost = this.currentDaySalesSummary.itemSummary.filter((i) => i.cost > 0);
		const previousDays = this.venueService.salesSummaryList.value.filter((i) => i.checkDate !== this.checkDate && i.itemSummary);
		const previousDayItemsWithCost = previousDays.filter((i) => i.itemSummary).map((i) => i.itemSummary.filter((j) => j.cost > 0));
		const metric = {
			itemCost: 0,
			itemPercent: 0,
			sales: 0,
		};

		this.metrics.cost = currentDayItemsWithCost.reduce((acc, curr) => acc + curr.cost, 0);

		metric.sales = previousDays.reduce((acc, curr) => acc + curr.netSales, 0);

		previousDayItemsWithCost.forEach((day) => {
			metric.itemCost += day.reduce((acc, curr) => acc + curr.cost, 0);
		});
		this.metrics.recordsRemaining = previousDays.length;

		this.metrics.historicalCostPercent = metric.itemCost / metric.sales;
		this.metrics.costPercent = this.metrics.cost / this.currentDaySalesSummary.netSales;

		this.metrics.highestCostItemSummary = currentDayItemsWithCost.sort((a, b) => (a.cost < b.cost ? 1 : -1))[0];

		this.metrics.loadComplete = true;
	}

	onDoughnutChartPieceSelect(event) {
		if (event.active && event.active.length > 0) {
			const superGroupIndex = event.active[0].index;
			this.buildItemDoughnut(this.superGroupSummary[superGroupIndex].id);
		} else {
			this.buildSuperGroupDoughnut();
		}
	}
}
